import { RiCompass4Line, Stack } from 'component-library';
import DOMPurify from 'dompurify';
import { useTranslation } from 'react-i18next';

import { ConservationFactLabelNameEnum, TrendLabel } from '@/api/rest/resources/conservation';
import { ConservationExplainerWell, NCCardSectionHeader } from '@/components/NCCard/NCCard';
import { getNCChangeIcon } from '@/components/NCFactCapsule/NCFactCapsule';
import { FactCapsule } from '@/pages/shared/projects/pages/project/pages/conservation/shared/components/FactCapsule';
import { useFact } from '@/pages/shared/projects/pages/project/pages/conservation/shared/hooks/useFact';
import { useLatestAnalysisDatestamp } from '@/pages/shared/projects/pages/project/pages/conservation/shared/hooks/useLatestAnalysisDatestamp';

export const StatsTile = ({
  latestFact,
  changeFact,
}: {
  latestFact: ReturnType<typeof useFact<number>>;
  changeFact: ReturnType<typeof useFact<number>>;
}) => {
  const { t } = useTranslation();

  const currentYear = useFact<number>('r1_conservation_current_year');

  const customExplainerText = useFact<string>('r1_conservation_species_occupancy_historic_explainer');

  const changeFactSignificanceLabel = changeFact.labels?.find(
    (label) => label.name === ConservationFactLabelNameEnum.significance,
  );

  const isChangeFactNonSignificant =
    changeFactSignificanceLabel && changeFactSignificanceLabel.value === TrendLabel.stable;

  const latestAnalysisDatestamp = useLatestAnalysisDatestamp(
    latestFact?.measured_at ? { facts: latestFact } : undefined,
  );

  const fallbackExplainerText = changeFactSignificanceLabel?.value
    ? t(
        `shared.projects.project.conservation.explainers.speciesOccupancy.significance.${changeFactSignificanceLabel.value as TrendLabel}`,
      )
    : null;

  return (
    <Stack spacing={10} className='px-6' data-testid='species-occupancy-stats-tile'>
      <Stack spacing={6}>
        <NCCardSectionHeader
          title={t('shared.ncaDetail.indicatorSpecies.occupancy.statsHeading')}
          supplementaryInfo={latestAnalysisDatestamp}
        />
        <Stack spacing={16} className='flex-wrap md:flex-row'>
          <FactCapsule
            label={t('shared.ncaDetail.indicatorSpecies.occupancy.explainers.averageValue.title', {
              year: currentYear?.value,
            })}
            fact={latestFact}
            capsuleIconProps={{
              icon: getNCChangeIcon(latestFact),
            }}
            infoPopoverProps={{
              title: t('shared.ncaDetail.indicatorSpecies.occupancy.explainers.averageValue.title', {
                year: currentYear?.value,
              }),
              body: t('shared.ncaDetail.indicatorSpecies.occupancy.explainers.averageValue.body', {
                year: currentYear?.value,
              }),
              popoverTriggerProps: {
                'data-testid': 'species-occupancy-latest-analysis-popover',
              },
            }}
          />
          <FactCapsule
            label={t('shared.ncaDetail.indicatorSpecies.occupancy.explainers.changeValue.title')}
            fact={changeFact}
            signed
            capsuleIconProps={{ icon: <RiCompass4Line />, variant: 'default' }}
            infoPopoverProps={{
              title: t('shared.ncaDetail.indicatorSpecies.occupancy.explainers.changeValue.title'),
              body: t('shared.ncaDetail.indicatorSpecies.occupancy.explainers.changeValue.body'),
              popoverTriggerProps: {
                'data-testid': 'species-occupancy-change-value-popover',
              },
            }}
            nonSignificantDifferences={isChangeFactNonSignificant}
          />
        </Stack>
      </Stack>
      {(customExplainerText?.value || fallbackExplainerText) && (
        <ConservationExplainerWell
          title={t('global.whatDoesThisMean')}
          body={
            customExplainerText?.value ? (
              <div
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(customExplainerText.value),
                }}
              />
            ) : (
              <span className='whitespace-pre-wrap'>{fallbackExplainerText}</span>
            )
          }
        />
      )}
    </Stack>
  );
};
